import decodeJWT from 'jwt-decode'
import debounce from 'debounce'
import { IAuth, IRight, IAuthorizationRoles, IPermissions } from '../types/auth'
import {
  API,
  AUTH_TOKEN_RE,
  CACHE_PREFIX,
  AUTH,
  STAGE,
  Stages,
} from '../config'
import { confirm } from '../utils/confirm'

// TODO: consider moving constants to a separate file @Max
export enum RIGHT_CONSTANTS {
  PORTAL_PREFIX = 'GFX_',
  SYSTEM_RIGHT_LEVEL = 'sys',
  GROUP_RIGHT_LEVEL = 'grp',
  ADMIN_RIGHT_PERMISSION = 'admin',
  EDITOR_RIGHT_PERMISSION = 'editor',
  APPROVERS_RIGHT_PERMISSION = 'approvers',
  SUPPORT_RIGHT_PERMISSION = 'support',
  READ = 'read',
  WRITE = 'write',
}

export enum AREA_CONSTANTS {
  SETTINGS_ALL_GROUPS = 'SETTINGS_ALL_GROUPS',
  SETTINGS_GROUP = 'SETTINGS_GROUP',
  SETTINGS_CREATE_GROUP = 'SETTINGS_CREATE_GROUP',
  SETTINGS_REORDER_GROUPS = 'SETTINGS_REORDER_GROUPS',
  SETTINGS_GROUP_CAPABILITIES = 'SETTINGS_GROUP_CAPABILITIES',
  SETTINGS_ALL_CAPABILITIES = 'SETTINGS_ALL_CAPABILITIES',
  SETTINGS_CAPABILITY = 'SETTINGS_CAPABILITY',
  SETTINGS_CREATE_CAPABILITY = 'SETTINGS_CREATE_CAPABILITY',
  SETTINGS_ALL_MEDIA_TYPES = 'SETTINGS_ALL_MEDIA_TYPES',
  SETTINGS_MEDIA_TYPE = 'SETTINGS_MEDIA_TYPE',
  SETTINGS_CREATE_MEDIA_TYPE = 'SETTINGS_CREATE_MEDIA_TYPE',
  SETTINGS_MEDIA_TYPE_PRIORITIES = 'SETTINGS_MEDIA_TYPE_PRIORITIES',
  SETTINGS_ALL_MEDIA_TYPE_GROUPS = 'SETTINGS_ALL_MEDIA_TYPE_GROUPS',
  SETTINGS_MEDIA_TYPE_GROUP = 'SETTINGS_MEDIA_TYPE_GROUP',
  SETTINGS_CREATE_MEDIA_TYPE_GROUP = 'SETTINGS_CREATE_MEDIA_TYPE_GROUP',
  SETTINGS_MEDIA_TYPE_GROUP_TYPES = 'SETTINGS_MEDIA_TYPE_GROUP_TYPES',
  GROUP_PUBLISHING_AREA = 'GROUP_PUBLISHING_AREA',
  CREATE_PACK = 'CREATE_PACK',
  VIEW_PACK = 'VIEW_PACK',
  PUBLISHED_SEARCH = 'PUBLISHED_SEARCH',
}

export const accessibleAreasPerRoleAndAction = {
  [RIGHT_CONSTANTS.EDITOR_RIGHT_PERMISSION]: {
    [RIGHT_CONSTANTS.READ]: [
      AREA_CONSTANTS.GROUP_PUBLISHING_AREA,
      AREA_CONSTANTS.CREATE_PACK,
      AREA_CONSTANTS.VIEW_PACK,
      AREA_CONSTANTS.PUBLISHED_SEARCH,
    ],
    [RIGHT_CONSTANTS.WRITE]: [
      AREA_CONSTANTS.GROUP_PUBLISHING_AREA,
      AREA_CONSTANTS.CREATE_PACK,
      AREA_CONSTANTS.VIEW_PACK,
      AREA_CONSTANTS.PUBLISHED_SEARCH,
    ],
  },
  [RIGHT_CONSTANTS.SUPPORT_RIGHT_PERMISSION]: {
    [RIGHT_CONSTANTS.READ]: [
      AREA_CONSTANTS.SETTINGS_ALL_GROUPS,
      AREA_CONSTANTS.SETTINGS_GROUP,
      AREA_CONSTANTS.SETTINGS_GROUP_CAPABILITIES,
      AREA_CONSTANTS.SETTINGS_ALL_CAPABILITIES,
      AREA_CONSTANTS.SETTINGS_CAPABILITY,
      AREA_CONSTANTS.SETTINGS_ALL_MEDIA_TYPES,
      AREA_CONSTANTS.SETTINGS_MEDIA_TYPE,
      AREA_CONSTANTS.SETTINGS_ALL_MEDIA_TYPE_GROUPS,
      AREA_CONSTANTS.SETTINGS_MEDIA_TYPE_GROUP,
      AREA_CONSTANTS.SETTINGS_MEDIA_TYPE_GROUP_TYPES,
    ],
    [RIGHT_CONSTANTS.WRITE]: [
      AREA_CONSTANTS.SETTINGS_ALL_GROUPS,
      AREA_CONSTANTS.SETTINGS_ALL_CAPABILITIES,
      AREA_CONSTANTS.SETTINGS_ALL_MEDIA_TYPES,
      AREA_CONSTANTS.SETTINGS_ALL_MEDIA_TYPE_GROUPS,
    ],
  },
  [RIGHT_CONSTANTS.APPROVERS_RIGHT_PERMISSION]: {
    [RIGHT_CONSTANTS.READ]: [
      AREA_CONSTANTS.SETTINGS_CREATE_MEDIA_TYPE,
      AREA_CONSTANTS.SETTINGS_MEDIA_TYPE_PRIORITIES,
      AREA_CONSTANTS.SETTINGS_CREATE_MEDIA_TYPE_GROUP,
    ],
    [RIGHT_CONSTANTS.WRITE]: [
      AREA_CONSTANTS.SETTINGS_MEDIA_TYPE,
      AREA_CONSTANTS.SETTINGS_MEDIA_TYPE_PRIORITIES,
      AREA_CONSTANTS.SETTINGS_CREATE_MEDIA_TYPE,
      AREA_CONSTANTS.SETTINGS_CREATE_MEDIA_TYPE_GROUP,
      AREA_CONSTANTS.SETTINGS_MEDIA_TYPE_GROUP_TYPES,
    ],
  },
  [RIGHT_CONSTANTS.ADMIN_RIGHT_PERMISSION]: {
    [RIGHT_CONSTANTS.READ]: [
      AREA_CONSTANTS.SETTINGS_CREATE_GROUP,
      AREA_CONSTANTS.SETTINGS_REORDER_GROUPS,
      AREA_CONSTANTS.SETTINGS_CREATE_CAPABILITY,
    ],
    [RIGHT_CONSTANTS.WRITE]: [
      AREA_CONSTANTS.SETTINGS_GROUP,
      AREA_CONSTANTS.SETTINGS_CREATE_GROUP,
      AREA_CONSTANTS.SETTINGS_REORDER_GROUPS,
      AREA_CONSTANTS.SETTINGS_GROUP_CAPABILITIES,
      AREA_CONSTANTS.SETTINGS_CAPABILITY,
      AREA_CONSTANTS.SETTINGS_CREATE_CAPABILITY,
      AREA_CONSTANTS.SETTINGS_MEDIA_TYPE_GROUP,
    ],
  },
}

const parseRights = (rights: string[]): IRight[] =>
  rights.map(
    (right: string): IRight => {
      const [level = '', permission = '', groupName = ''] = right
        .replace(RIGHT_CONSTANTS.PORTAL_PREFIX, '')
        .toLowerCase()
        .split('_')
      return { level, permission, groupName }
    }
  )

const getPermissions = (parsedRights: IRight[]): IPermissions => {
  const authorizationRoles: IAuthorizationRoles = {
    isSystemAdmin: parsedRights.some(
      (right: IRight) =>
        right.level === RIGHT_CONSTANTS.SYSTEM_RIGHT_LEVEL &&
        right.permission === RIGHT_CONSTANTS.ADMIN_RIGHT_PERMISSION
    ),
    isSystemApprover: parsedRights.some(
      (right: IRight) =>
        right.level === RIGHT_CONSTANTS.SYSTEM_RIGHT_LEVEL &&
        right.permission === RIGHT_CONSTANTS.APPROVERS_RIGHT_PERMISSION
    ),
    isSystemSupport: parsedRights.some(
      (right: IRight) =>
        right.level === RIGHT_CONSTANTS.SYSTEM_RIGHT_LEVEL &&
        right.permission === RIGHT_CONSTANTS.SUPPORT_RIGHT_PERMISSION
    ),
  }
  return {
    ...authorizationRoles,
    editorForGroups: parsedRights
      .filter(
        (right: IRight) =>
          right.level === RIGHT_CONSTANTS.GROUP_RIGHT_LEVEL &&
          right.permission === RIGHT_CONSTANTS.EDITOR_RIGHT_PERMISSION &&
          right.groupName
      )
      .map((right: IRight): string => right.groupName as string),
    restrictedAreasReadable: getAllowedRestrictedAreas(
      RIGHT_CONSTANTS.READ,
      authorizationRoles
    ),
    restrictedAreasWritable: getAllowedRestrictedAreas(
      RIGHT_CONSTANTS.WRITE,
      authorizationRoles
    ),
  }
}

const getAllowedRestrictedAreas = (
  authorization: string,
  { isSystemAdmin, isSystemApprover, isSystemSupport }: IAuthorizationRoles
): string[] => {
  return isSystemAdmin
    ? [
      ...accessibleAreasPerRoleAndAction[
      RIGHT_CONSTANTS.ADMIN_RIGHT_PERMISSION
      ][authorization],
      ...accessibleAreasPerRoleAndAction[
      RIGHT_CONSTANTS.APPROVERS_RIGHT_PERMISSION
      ][authorization],
      ...accessibleAreasPerRoleAndAction[
      RIGHT_CONSTANTS.SUPPORT_RIGHT_PERMISSION
      ][authorization],
      ...accessibleAreasPerRoleAndAction[
      RIGHT_CONSTANTS.EDITOR_RIGHT_PERMISSION
      ][authorization],
    ]
    : isSystemApprover
      ? [
        ...accessibleAreasPerRoleAndAction[
        RIGHT_CONSTANTS.APPROVERS_RIGHT_PERMISSION
        ][authorization],
        ...accessibleAreasPerRoleAndAction[
        RIGHT_CONSTANTS.SUPPORT_RIGHT_PERMISSION
        ][authorization],
        ...accessibleAreasPerRoleAndAction[
        RIGHT_CONSTANTS.EDITOR_RIGHT_PERMISSION
        ][authorization],
      ]
      : isSystemSupport
        ? [
          ...accessibleAreasPerRoleAndAction[
          RIGHT_CONSTANTS.SUPPORT_RIGHT_PERMISSION
          ][authorization],
          ...accessibleAreasPerRoleAndAction[
          RIGHT_CONSTANTS.EDITOR_RIGHT_PERMISSION
          ][authorization],
        ]
        : []
}

export const parseToken = (token: string): IAuth => {
  const { family_name, given_name, uid, rights: allRights } = decodeJWT(token)
  const rawRights = allRights.filter((right: string): boolean =>
    right.startsWith(RIGHT_CONSTANTS.PORTAL_PREFIX)
  )
  const parsedRights = parseRights(rawRights)
  const permissions = getPermissions(parsedRights)
  return {
    token,
    uid,
    family_name,
    given_name,
    rights: {
      raw: rawRights,
      parsed: parsedRights,
    },
    permissions,
  }
}

export const authToken = {
  get: (): string | undefined =>
    localStorage.auth_token,
  set: (token: string): void => {
    localStorage.setItem('auth_token', token)
  },
  remove: (): void => {
    localStorage.removeItem('auth_token')
  },
}

export const lastAuthRedirection = {
  storage: STAGE === Stages.local ? localStorage : sessionStorage,
  get(): number {
    return +(this.storage.getItem(AUTH.LAST_REDIRECTION) || 0)
  },
  set(time: number): void {
    this.storage.setItem(AUTH.LAST_REDIRECTION, `${time}`)
  },
  remove(): void {
    this.storage.removeItem(AUTH.LAST_REDIRECTION)
  },
}

export const clearCache = (): void => {
  [sessionStorage, localStorage].forEach((storage) =>
    Object.keys(storage).forEach((key) => {
      if (key.startsWith(CACHE_PREFIX)) {
        storage.removeItem(key)
      }
    })
  )
}

export const logout = (): void => {
  clearCache()
  window.location.assign(API.AUTH.SAFE_LOGOUT_URL)
}

export const decorateURLWithOctotorp = (url: string): string => {
  if (url === 'http://localhost:3000/') {
    return `${url}#/`
  }
  return url
}

export const getAuthUrl = (): string =>
  `${API.AUTH.SAFE_LOGIN_URL}/?redirect=${encodeURIComponent(
    decorateURLWithOctotorp(window.location.href)
  )}${API.AUTH.STRATEGY}`

export const refreshPageConditionally = debounce(() => {
  confirm(
    'There are some problems with the authentication service. Refresh the page?'
  ).then(() => {
    lastAuthRedirection.remove()
    window.location.reload()
  })
}, 1000)

export const redirectToSphinx = (): void => {
  const now = Date.now()
  if (
    !lastAuthRedirection.get() ||
    now - lastAuthRedirection.get() > AUTH.REDIRECT_TIMEOUT
  ) {
    window.location.assign(getAuthUrl())
    lastAuthRedirection.set(now)
  } else {
    refreshPageConditionally()
  }
}

export const extractTokenFromUrl = (): string | null => {
  const match = window.location.href.match(AUTH_TOKEN_RE)
  return match && match[1]
}

export const retrieveGroupId = (props: any): string => {
  const { match, computedMatch, groupId } = props
  const matchObject = match || computedMatch
  const matchedGroupId =
    matchObject && matchObject.params && matchObject.params.groupId
  return groupId || matchedGroupId || ''
}
